import {useState,useEffect} from 'react'
import {useSelector,useDispatch} from 'react-redux'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import InputGroup from 'react-bootstrap/InputGroup'
import Tooltip from 'react-bootstrap/Tooltip'
import Spinner from 'react-bootstrap/Spinner'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'

import useS3DirectUpload from '../../../hooks/useS3DirectUpload'

import Select from 'react-select'

import {
	deactivateSubscriptionPrice,
	selectAvailableSubscriptionById,
	updateSubscription,
	createSubscription,
	setActiveComponent,
	setEditor
} from './availableSubscriptionsSlice'
import useApiStatusDispatch from '../../../hooks/useApiStatusDispatch'
import PriceOption from './PriceOption'
import {selectAcceptedCurrency} from '../../settings/settingsSlice'
import {getCurrencySymbol} from '../../../util/intl'

import {
	useCreatePurchaseForm,
	useRemovePurchaseForm,
	usePurchaseItemForms
} from '../hooks'

const Editor = () => {
	const dispatch = useDispatch()
	const statusDispatch = useApiStatusDispatch()
	const id = useSelector(state=>state.availableSubscriptions.editor.id)
	const isNew = !Boolean(id)
	const currency = useSelector(selectAcceptedCurrency)

	const {
		status:subscriptionFormsStatus,
		forms:subscriptionForms,
		allForms
	} = usePurchaseItemForms('subscription',id)

	const createPurchaseForm = useCreatePurchaseForm()
	const removePurchaseForm = useRemovePurchaseForm()

	const [status,setStatus] = useState('idle')
	//if subscrition id is set then get the subscription from the state
	const subscription = useSelector(state=>isNew?false:selectAvailableSubscriptionById(state,id))
	const [name,setName] = useState('')
	const [currentImage,setCurrentImage] = useState('')
	const [newImage,setNewImage] = useState(null)
	const [notes,setNotes] = useState('')
	const [isActive,setIsActive] = useState(false)
	const [useSessionCredits,setUseSessionCredits] = useState(false)
	const [sessionCredits,setSessionCredits] = useState(0)
	const [hasPrice,setHasPrice] = useState(false)
	const [interval,setInterval] = useState('month')
	const [intervalCount,setIntervalCount] = useState(1)
	const [dollarAmount,setDollarAmount] = useState('')

	const [removePrice, setRemovePrice] = useState(false)

	//if user reloads the page then available subscriptions will
	//not be in the state. detect this and load them if needed
	useEffect(()=>{
		if(!isNew&&!subscription){
		}
	},[])

	useEffect(()=>{
		if(isNew||!subscription) return
		setName(subscription.name)
		setNotes(subscription.notes)
		setCurrentImage(subscription.image)
		setIsActive(Boolean(subscription?.is_active))
		setUseSessionCredits(subscription?.session_credits>0)
		setSessionCredits(subscription.session_credits)
		setHasPrice(Boolean(subscription.price))
		if(Boolean(subscription.price)){
			setInterval(subscription.price.recurring.interval)
			setIntervalCount(subscription.price.recurring.interval_count)
			setDollarAmount(subscription.price.dollar_amount)
		}
	},[subscription])

	const {processUpload} = useS3DirectUpload()

	const closeEditor = ()=>{
		dispatch(setActiveComponent('subscriptions'))
		dispatch(setEditor({show:false,id:null}))
	}

	const handleCreateSubscription = ()=>{
		setStatus('pending')
		const price = {
			recurring:{
				interval_count:intervalCount,
				interval
			},
			dollar_amount:dollarAmount,
		}
		statusDispatch(createSubscription({
			session_credits:useSessionCredits?sessionCredits:0,
			is_active:isActive?1:0,
			price,
			notes,
			name,
		}),'subscription','create').then(action=>{
			setStatus('idle')
			if(action.payload.success==='1'){
				closeEditor()
			}
		})
	}

	const handleUpdateSubscription = async ()=>{
		setStatus('pending')
		const postData = {
			session_credits:useSessionCredits?sessionCredits:0,
			is_active:isActive?1:0,
			name,notes,id,
			image:currentImage
		}
		if(newImage){
			const s3Res = await processUpload(newImage)
			postData.image = s3Res.payload.url
		}
		if(removePrice){
			dispatch(deactivateSubscriptionPrice({
				price_id:subscription?.price.id,
				id
			})).then(()=>{
				//due to the way stripe handles prices
				//we must only set price data if there isn't already
				//a price and we actually have all the price data
				if(!hasPrice&&dollarAmount!=''){
					postData.price = {
						recurring:{
							interval_count:intervalCount,
							interval
						},
						dollar_amount:dollarAmount,
					}
				}
				statusDispatch(
					updateSubscription(postData),
					'subscription',
					'edit'
				).then(action=>{
					setStatus('idle')
					if(action.payload.success==='1'){
						closeEditor()
					}
				})
			})
		} else {
			if(!hasPrice&&dollarAmount!=''){
				postData.price = {
					recurring:{
						interval_count:intervalCount,
						interval
					},
					dollar_amount:dollarAmount,
				}
			}
			statusDispatch(
				updateSubscription(postData),
				'subscription',
				'edit'
			).then(action=>{
				setStatus('idle')
				console.log(action)
				if(action.payload.success==='1'){
					closeEditor()
				}
			})
		}
	}

	const handleRemovePrice = ()=>{
		if(!window.confirm('Are you sure?')) return
		setHasPrice(false)
		setRemovePrice(true)
	}

	const validate = ()=>{
		const needed = []
		if(!name) needed.push('subscription name')
		if(!dollarAmount) needed.push('price')
		if(!intervalCount) needed.push('payment frequency')
		return needed
	}

	
	const needed = validate()

	const saveHandler =  (
		isNew ?
		handleCreateSubscription :
		handleUpdateSubscription
	)

	return (
		<div id="containerDiv">
			<div className="bg-white p-3 mt-2 border rounded">
				<strong style={{lineHeight:'1.5'}}>
					{isNew?'Create Subscription':'Edit Subscription'}
				</strong>
				<hr className="my-1"/>
				<label>Subscription Name</label>
				<Form.Control
					onChange={e=>{setName(e.target.value)}}
					value={name}
				/>
				<label>Subscription Image</label>
				{(Boolean(currentImage)&&!newImage)&&
					<div className="mb-2">
						<img src={currentImage} className="d-block" style={{
							width:'300px',
							maxWidth:'100%'
						}}/>
						<div className="mb-1">
							<Button 
								onClick={setCurrentImage.bind(null,'')}
								variant="outline-danger" 
								size="sm" 
								className="mr-2"
							>
								Remove Image
							</Button>
						</div>
						<Form.File
							id="update-subscription-image-input"
							label="Select new image"
							onChange={e=>{
								setNewImage(e.target.files[0])
							}}
							custom
						/>
					</div>
				}
				{(!Boolean(currentImage)&&!newImage)&&
					<Form.File
							id="update-subscription-image-input"
							label="Select image"
							onChange={e=>{
								setNewImage(e.target.files[0])
							}}
							custom
						/>
				}
				{(!Boolean(currentImage)&&Boolean(subscription.image))&&
					<div>
						<strong className="d-block">You are removing the current image</strong>
						<Button
							onClick={setCurrentImage.bind(null,subscription.image)}
							variant="outline-success"
						>
							Keep Using the Current Image
						</Button>
					</div>
				}
				{Boolean(newImage)&&
					<div>
						<img 
							className="rounded mb-2"
							src={window.URL.createObjectURL(newImage)} 
							style={{width:'300px',maxWidth:'100%',display:'block'}}
						/>
						<Button 
							onClick={()=>{ setNewImage(null) }}
							variant="link" 
							className="text-danger"
						>
							Remove New Image
						</Button>
					</div>
				}
				<label>Subscription Notes</label>
				<Form.Control 
					onChange={e=>{setNotes(e.target.value)}}
					as="textarea"
					value={notes}
				/>
				<Form.Switch 
					label="Active"
					onChange={e=>{
						setIsActive(e.target.checked)
					}}
					checked={isActive}
					id="is-active"
				/>
				<div>
					{
						hasPrice ?
						<div className="d-flex">
							<span className="mr-2">Pricing:</span>
							<PriceOption price={subscription?.price}/>
							<OverlayTrigger overlay={
								<Tooltip>
									This will remove the pricing option so that you can add another
								</Tooltip>
							}>
								<Button 
									onClick={handleRemovePrice}
									variant="outline-danger" 
									className="ml-2" 
									size="sm"
								>
									Remove Pricing
								</Button>
							</OverlayTrigger>
						</div>
						:
						<>
							<div>Pricing</div>
							<InputGroup>
								<InputGroup.Prepend>
									<InputGroup.Text>
										{getCurrencySymbol(currency?.ISO)}
									</InputGroup.Text>
								</InputGroup.Prepend>
								<Form.Control 
									onChange={e=>{
										setDollarAmount(e.target.value)
									}}
									placeholder={`XX.XX (${currency?.ISO})`} 
									value={dollarAmount}
								/>
								<InputGroup.Text style={{borderRadius:0,borderLeft:'none',borderRight:'none'}}>
									every
								</InputGroup.Text>
								<Form.Control 
									onChange={e=>{
										setIntervalCount(e.target.value)
									}}
									value={intervalCount}
								/>
								<Form.Control 
									onChange={e=>{
										setInterval(e.target.value)
									}}
									value={interval}
									as="select"
								>
									<option value="day">day(s)</option>
									<option value="week">week(s)</option>
									<option value="month">month(s)</option>
									<option value="year">year(s)</option>
								</Form.Control>
							</InputGroup>
							<small className="text-muted">Example: $340 every 2 months</small>
						</>
					}
				</div>
				<Form.Check 
					onChange={e=>{
						setUseSessionCredits(e.target.checked)
					}}
					label="Use session credits" 
					checked={useSessionCredits}
					id="session-credits"
				/>
				{useSessionCredits&&
					<>
						<label>Session Credits</label> 
						<Form.Control
							onChange={e=>{
								setSessionCredits(e.target.value)
							}}
							placeholder="# of Session Credits"
							value={sessionCredits}
						/>
					</>
				}
				<label>Forms & Waivers</label>
				<Select 
					onChange={(_,payload)=>{
						switch(payload.action){
							case 'select-option':
								createPurchaseForm({
									itemType:'subscription',
									itemId:id,
									formId:payload.option.value
								})
								break;
							case 'remove-value':
								removePurchaseForm({
									itemType:'subscription',
									itemId:id,
									formId:payload.removedValue.value
								})
								break;
						}
					}}
					value={
						subscriptionForms.map(
							form => ({
								value:form.form_id,
								label:form.form_title
							})
						)//end map
					}
					options={
						allForms.map(
							form => ({
								value:form.form_id,
								label:form.form_title
							})
						)//end map
					}//end options
					isMulti
				/>
				<div className="text-right mt-3">
					<Button className="mr-2" size="sm" onClick={closeEditor} variant="outline-danger">
						Cancel
					</Button>
					{
						needed.length>0?
						<div className="text-primary">
							Please provide: {needed.join(', ')}
						</div>
						:
						<Button size="sm" 
							disabled={status==='pending'}
							variant="outline-success" 
							onClick={saveHandler}
						>
							{isNew?'Create':'Update'}
							{status==='pending'&&<Spinner className="ml-1" animation="border" size="sm"/>}
						</Button>
					}
				</div>
			</div>
		</div>
	)
}

export default Editor